import React, { useState, useRef, useEffect } from 'react';
import { User, Camera, Mail, ArrowLeft, Check, Home } from 'lucide-react';
import { auth } from '../firebase';
import { updateProfile } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import AvatarPicker from '../components/ui/AvatarPicker';
import GeometricBackground from '../components/ui/GeometricBackground';
import GrammarBottomNav from '../components/grammar/GrammarBottomNav';

const TEAL = '#006D77';
const SAGE = '#83C5BE';
const TEXT_PRIMARY = '#121212';
const TEXT_NEUTRAL = '#636E72';
const BORDER_CARD = '#E1E4E8';
const SHADOW_BENTO = '0 4px 20px rgba(0,0,0,0.05)';

const ProfileEditPage = ({ onNavigate, onAvatarUpdate }) => {
  const { t } = useTranslation();
  const currentUser = auth.currentUser;
  const displayName = currentUser?.displayName || '';
  const nameParts = displayName.split(' ');
  const defaultFirstName = nameParts[0] || '';
  const defaultLastName = nameParts.slice(1).join(' ') || '';

  const [firstName, setFirstName] = useState(defaultFirstName);
  const [lastName, setLastName] = useState(defaultLastName);
  const [saveStatus, setSaveStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setFirstName(defaultFirstName);
    setLastName(defaultLastName);
  }, [currentUser?.displayName]);

  const handleProfileSave = async () => {
    if (!currentUser) return;
    setIsLoading(true);
    setSaveStatus(t('settings.status.updatingProfile'));
    try {
      const newDisplayName = `${firstName.trim()} ${lastName.trim()}`.trim();
      if (newDisplayName !== currentUser.displayName) {
        await updateProfile(currentUser, { displayName: newDisplayName });
      }
      setSaveStatus(t('settings.status.profileUpdated'));
      setTimeout(() => {
        setSaveStatus('');
        onNavigate && onNavigate('/profile');
      }, 1500);
    } catch (error) {
      console.error('Error updating profile:', error);
      setSaveStatus(t('settings.status.errorUpdatingProfile'));
      setTimeout(() => setSaveStatus(''), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAvatarSelect = (avatarUrl) => {
    if (onAvatarUpdate) {
      onAvatarUpdate({ isAvatarUrl: true, url: avatarUrl });
    }
    setShowAvatarPicker(false);
    setSaveStatus(t('settings.status.avatarUpdated'));
    setTimeout(() => setSaveStatus(''), 3000);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert(t('settings.alerts.selectImage'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert(t('settings.alerts.imageTooLarge'));
      return;
    }
    if (onAvatarUpdate) onAvatarUpdate(file);
    setSaveStatus(t('settings.status.avatarUploading'));
    setTimeout(() => setSaveStatus(t('settings.status.avatarUpdated')), 2000);
  };

  return (
    <div className="animate-fade-in min-h-full safe-area-inset relative overflow-hidden pb-32 sm:pb-0" style={{ backgroundColor: '#fafafa' }}>
      <GeometricBackground />

      {/* Mobile: bottom padding (pb-32) clears fixed bottom nav (h-16 + safe-area) so Save Changes is always visible */}
      <div className="relative z-10 max-w-2xl mx-auto pt-2 sm:pt-4 pb-6 sm:pb-8 px-0 sm:px-6 lg:px-8">
        {/* Header, small side padding on mobile so title doesn’t touch edges */}
        <div className="mb-4 sm:mb-6 px-4 sm:px-0">
          <button
            type="button"
            onClick={() => onNavigate && onNavigate('/profile')}
            className="hidden sm:flex items-center gap-2 px-4 py-2 min-h-[48px] rounded-2xl border-2 bg-white font-semibold transition-transform active:scale-95 touch-manipulation mb-3"
            style={{ borderColor: BORDER_CARD, color: TEXT_PRIMARY }}
          >
            <ArrowLeft className="w-5 h-5" />
            {t('nav.back', { defaultValue: 'Back' })}
          </button>
          <h1 className="text-xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-relaxed" style={{ color: TEXT_PRIMARY }}>
            {t('settings.profile.title')}
          </h1>
        </div>

        {/* Edit profile box, full-width on mobile (edge-to-edge), rounded on sm+; inner padding keeps content clear */}
        <div className="rounded-none sm:rounded-2xl bg-white border-y sm:border p-4 sm:p-6 w-full min-w-0" style={{ borderColor: BORDER_CARD, boxShadow: SHADOW_BENTO }}>
          <div className="space-y-4 sm:space-y-6">
            {/* Avatar, picture left, buttons right, compact */}
            <div>
              <label className="block text-sm font-semibold mb-2 leading-relaxed" style={{ color: TEXT_PRIMARY }}>
                {t('settings.profile.profilePicture')}
              </label>
              <div className="flex items-center gap-4">
                <div className="relative flex-shrink-0">
                  <img
                    className="h-20 w-20 sm:h-24 sm:w-24 lg:h-28 lg:w-28 rounded-full border-2 object-cover"
                    style={{ borderColor: TEAL }}
                    src={currentUser?.photoURL || `https://placehold.co/144x144/006D77/ffffff?text=${(firstName[0] || 'U')}${(lastName[0] || '')}`}
                    alt="Profile"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute -bottom-0.5 -right-0.5 w-8 h-8 sm:w-9 sm:h-9 bg-white rounded-full flex items-center justify-center border-2 transition-transform active:scale-95 touch-manipulation shadow-sm"
                    style={{ borderColor: TEAL, color: TEXT_PRIMARY }}
                    title={t('settings.profile.uploadCustomPhoto')}
                  >
                    <Camera className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex flex-col gap-2 min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => setShowAvatarPicker(true)}
                    className="min-h-[48px] px-4 py-2.5 bg-white font-semibold rounded-2xl border-2 transition-transform active:scale-95 touch-manipulation text-sm sm:text-base text-left"
                    style={{ borderColor: BORDER_CARD, color: TEAL }}
                  >
                    {t('settings.profile.chooseAvatar')}
                  </button>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="min-h-[48px] px-4 py-2.5 bg-white font-semibold rounded-2xl border-2 transition-transform active:scale-95 touch-manipulation text-sm sm:text-base text-left"
                    style={{ borderColor: BORDER_CARD, color: TEAL }}
                  >
                    {t('settings.profile.uploadPhoto')}
                  </button>
                </div>
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
              <p className="text-xs mt-2 leading-relaxed" style={{ color: TEXT_NEUTRAL }}>{t('settings.profile.imageFormat')}</p>
            </div>

            {/* First / Last name, 8pt grid, thumb-friendly inputs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-semibold mb-2 leading-relaxed" style={{ color: TEXT_PRIMARY }}>{t('settings.profile.firstName')}</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <User className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: TEXT_NEUTRAL }} />
                  </div>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="block w-full pl-11 sm:pl-12 pr-4 py-3 text-base border-2 rounded-2xl shadow-sm transition-all touch-manipulation min-h-[48px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#006D77]/30 focus:border-[#006D77]"
                    style={{ borderColor: BORDER_CARD, color: TEXT_PRIMARY }}
                    placeholder={defaultFirstName || t('settings.profile.firstNamePlaceholder')}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2 leading-relaxed" style={{ color: TEXT_PRIMARY }}>{t('settings.profile.lastName')}</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <User className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: TEXT_NEUTRAL }} />
                  </div>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="block w-full pl-11 sm:pl-12 pr-4 py-3 text-base border-2 rounded-2xl shadow-sm transition-all touch-manipulation min-h-[48px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#006D77]/30 focus:border-[#006D77]"
                    style={{ borderColor: BORDER_CARD, color: TEXT_PRIMARY }}
                    placeholder={defaultLastName || t('settings.profile.lastNamePlaceholder')}
                  />
                </div>
              </div>
            </div>

            {/* Email read-only */}
            <div>
              <label className="block text-sm font-semibold mb-2 leading-relaxed" style={{ color: TEXT_PRIMARY }}>{t('settings.profile.email')}</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: TEXT_NEUTRAL }} />
                </div>
                <input
                  type="email"
                  value={currentUser?.email || ''}
                  disabled
                  className="block w-full pl-11 sm:pl-12 pr-4 py-3 text-base rounded-2xl border-2 cursor-not-allowed touch-manipulation min-h-[48px] leading-relaxed"
                  style={{ backgroundColor: '#f5f5f5', borderColor: BORDER_CARD, color: TEXT_NEUTRAL }}
                />
              </div>
              <p className="text-xs mt-1 sm:mt-2 leading-relaxed" style={{ color: TEXT_NEUTRAL }}>{t('settings.profile.emailCannotChange')}</p>
            </div>
          </div>

          {/* Save area, thumb zone, Sage success state */}
          <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4" style={{ borderColor: BORDER_CARD }}>
            {saveStatus && (
              <span className="text-sm font-medium flex items-center gap-2 order-2 sm:order-1 animate-fade-in" style={{ color: SAGE }}>
                <Check className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                {saveStatus}
              </span>
            )}
            <button
              type="button"
              onClick={handleProfileSave}
              disabled={isLoading}
              className="w-full sm:w-auto min-h-[48px] px-6 sm:px-10 py-3 sm:py-4 rounded-2xl font-bold text-base sm:text-lg transition-transform active:scale-95 touch-manipulation order-1 sm:order-2 flex items-center justify-center gap-2 sm:gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: isLoading ? BORDER_CARD : TEAL,
                color: isLoading ? TEXT_NEUTRAL : '#fff'
              }}
            >
              {isLoading ? t('settings.buttons.saving') : t('settings.buttons.saveChanges')}
              {!isLoading && <Check className="w-5 h-5 sm:w-6 sm:h-6" />}
            </button>
          </div>
        </div>
      </div>

      <GrammarBottomNav
        buttons={[
          { id: 'home', icon: Home, label: t('nav.home', { defaultValue: 'Home' }), onClick: () => onNavigate && onNavigate('/dashboard'), color: TEAL },
          { id: 'back', icon: ArrowLeft, label: t('nav.back', { defaultValue: 'Back' }), onClick: () => onNavigate && onNavigate('/profile'), color: TEAL }
        ]}
      />

      {showAvatarPicker && (
        <AvatarPicker
          currentAvatar={currentUser?.photoURL}
          onAvatarSelect={handleAvatarSelect}
          onClose={() => setShowAvatarPicker(false)}
        />
      )}
    </div>
  );
};

export default ProfileEditPage;
